import type { OrderedExcalidrawElement } from '@excalidraw/excalidraw/element/types';
import type { AppState } from '@excalidraw/excalidraw/types';
import { useSyncExternalStore, type ReactNode } from 'react';

/**
 * Pan / zoom vs everything else. A pinch fires Excalidraw's onChange ~120×/s; re-rendering the whole
 * board page (estimate panel, toolbar, zone membership) on each made pinch-zoom choppy on a dense
 * board. The page re-renders when `viewKey` changes; only the screen-space overlays follow the
 * camera, through a CameraLayer ticking off a CameraClock.
 */
export interface CameraClock {
  now(): number;
  bump(): void;
  subscribe(fn: () => void): () => void;
}

export function cameraClock(): CameraClock {
  let n = 0;
  const fns = new Set<() => void>();
  return {
    now: () => n,
    bump() {
      n++;
      fns.forEach((f) => f());
    },
    subscribe(fn) {
      fns.add(fn);
      return () => fns.delete(fn);
    },
  };
}

/** Renders `children(tick)` again on every camera bump, without re-rendering its parent. */
export function CameraLayer({ clock, children }: { clock: CameraClock; children(tick: number): ReactNode }) {
  const tick = useSyncExternalStore(clock.subscribe, clock.now);
  return <>{children(tick)}</>;
}

/**
 * Which scene this is: a new elements array (Excalidraw builds one on every scene update, including
 * updateScene with elements whose version wasn't bumped) or a changed versionNonce (an in-place
 * mutateElement) both count. djb2 over the nonces is Excalidraw's hashElementsVersion, without its runtime.
 */
const arrays = new WeakMap<readonly unknown[], number>();
let nextArray = 0;
export function elementsVersion(els: readonly OrderedExcalidrawElement[]): string {
  let id = arrays.get(els);
  if (id === undefined) arrays.set(els, (id = ++nextArray));
  let h = 5381;
  for (const e of els) h = ((h << 5) + h + e.versionNonce) >>> 0;
  return `${id}.${h}`;
}

/**
 * Everything about the board except the camera (scrollX / scrollY / zoom): the elements' versions plus
 * the app state the page reads or shows (tool, selection, the line or text being edited, dialogs,
 * theme, canvas size). Equal keys → nothing the page renders from has changed.
 */
export function viewKey(els: readonly OrderedExcalidrawElement[], st: AppState): string {
  const tool = st.activeTool.type === 'custom' ? `custom:${st.activeTool.customType}` : st.activeTool.type;
  return [
    elementsVersion(els),
    st.editingLinearElement?.elementId ?? '',
    st.editingTextElement?.id ?? '',
    tool,
    Object.keys(st.selectedElementIds).join(','),
    st.openDialog?.name ?? '',
    st.theme,
    `${st.width}x${st.height}`,
    st.viewModeEnabled ? 'view' : '',
  ].join('|');
}
